import { forwardRef } from "react";

import { Button, ButtonProps } from "../Button";
import { usePopoverContext } from "./internal/usePopoverContext";
import { childrenToString } from "../../internal/functions";
import { DataTrackingId } from "../../types";
import { useTrackingId } from "../../hooks";

/**
 * Props for the PopoverClose component
 * @extends ButtonProps
 */
export type PopoverCloseProps = ButtonProps & DataTrackingId;

/**
 * PopoverClose component for closing the popover.
 *
 * Features:
 * - Automatic popover closing on click
 * - Supports all Button component props and styling
 * - Controlled and uncontrolled mode support
 * - Context integration with parent popover
 * - Accessibility support with proper button semantics
 * - Delayed closing for controlled mode
 *
 * @example
 * <Popover.Close>Close</Popover.Close>
 *
 * @example
 * <Popover.Close appearance="secondary" size="small">
 *   Cancel
 * </Popover.Close>
 *
 * @example
 * <Popover.Close onClick={() => console.log('Custom close handler')}>
 *   Close with custom handler
 * </Popover.Close>
 */
export const PopoverClose = forwardRef<HTMLButtonElement, PopoverCloseProps>(
  function PopoverClose(props, ref) {
    const { onClick, ...rest } = props;
    const context = usePopoverContext();

    const data = {
      children: childrenToString(props.children),
    };

    const trackingId = useTrackingId({
      name: "PopoverClose",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    return (
      <Button
        ref={ref}
        data-tracking-id={trackingId}
        data-anv="popover-close"
        {...rest}
        onClick={(event) => {
          context?.popover.close();
          context?.onClose?.();
          onClick?.(event);
        }}
      />
    );
  },
);

PopoverClose.displayName = "PopoverClose";
